import React, { useEffect } from "react";
import SafeIcon from "../../../common/SafeIcon";
import { FiX } from "react-icons/fi";

function formatDate(value) {
  if (!value) return "Not provided";
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
}

function formatMoney(value, currency) {
  if (value === null || value === undefined || value === "") return "Not available";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "Not available";

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(amount);
  } catch {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
    }).format(amount);
  }
}

function Detail({ label, children }) {
  return (
    <div className="min-w-0 rounded-xl bg-slate-50 p-3.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        {label}
      </p>
      <p className="mt-1.5 break-words text-sm font-semibold text-slate-800">
        {children || "Not provided"}
      </p>
    </div>
  );
}

export default function ClientTransactionDetailsModal({
  transaction,
  currency = "USD",
  onClose,
}) {
  useEffect(() => {
    if (!transaction) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [transaction, onClose]);

  if (!transaction) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-950/50 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="client-transaction-title"
        className="max-h-[92dvh] w-full overflow-y-auto rounded-t-3xl bg-white shadow-2xl sm:max-w-2xl sm:rounded-3xl"
      >
        <header className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-slate-100 bg-white px-5 py-5 sm:px-7">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-widest text-violet-700">
              Read-only transaction
            </p>
            <h2
              id="client-transaction-title"
              className="mt-1 break-words text-xl font-black text-slate-900"
            >
              Transaction details
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close transaction details"
            className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
          >
            <SafeIcon icon={FiX} className="text-lg" />
          </button>
        </header>

        <div className="grid gap-3 p-5 sm:grid-cols-2 sm:p-7">
          <Detail label="Date">{formatDate(transaction.date)}</Detail>
          <Detail label="Status">{transaction.status}</Detail>
          <Detail label="Description">{transaction.description}</Detail>
          <Detail label="Account">
            {transaction.account?.name}
            {transaction.account?.code
              ? ` · ${transaction.account.code}`
              : ""}
          </Detail>
          <Detail label="Reference / Batch">{transaction.batch_id}</Detail>
          <Detail label="Source">{transaction.source}</Detail>
          <Detail label="Debit">
            {formatMoney(transaction.debit, currency)}
          </Detail>
          <Detail label="Credit">
            {formatMoney(transaction.credit, currency)}
          </Detail>
          <Detail label="Transaction amount">
            {transaction.amount !== undefined
              ? formatMoney(transaction.amount, currency)
              : "Not stored on this ledger row"}
          </Detail>
          <Detail label="Transaction ID">{transaction.id}</Detail>
        </div>

        <footer className="border-t border-slate-100 px-5 py-4 text-right sm:px-7">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800"
          >
            Close
          </button>
        </footer>
      </section>
    </div>
  );
}